import type { MessageKey, Translator } from '../i18n';
import type { StyleEngine } from './style-engine';

export type FeatureGroup = 'text' | 'color' | 'navigation' | 'reading' | 'distraction';

export const GROUP_ORDER: readonly FeatureGroup[] = ['text', 'color', 'navigation', 'reading', 'distraction'];

export interface FeatureContext {
  doc: Document;
  styles: StyleEngine;
  /** Base URL of the widget's font files (a trailing `/` is added if missing), or null when fonts are not hosted. */
  fontBaseUrl?: string | null;
  /** z-index of the widget UI; page overlays must stay below it. */
  zIndex?: number;
  /** The panel's current translator, for text a feature puts on the page (the dictionary link). */
  getTranslator?: () => Translator;
}

/**
 * A user-facing adjustment.
 *
 * Resource contract: `apply` and `teardown` may only touch resources the feature owns — its own
 * style-engine id (equal to `id`), `data-pulxon-*` attributes and `pulxon-*` elements it created.
 * `apply` may be called again with another level while active; `teardown` must restore the page.
 * `enable` applies the new feature first and only then tears down conflicting features, so two
 * features must never share resources (a conflict's teardown would remove what the new one just applied).
 */
export interface FeatureDefinition {
  id: string;
  group: FeatureGroup;
  labelKey: MessageKey;
  levels: number;
  /** Optional status text per level; length must equal `levels`. */
  levelLabelKeys?: readonly MessageKey[];
  /** Declared in one direction; the registry makes conflicts symmetric. */
  conflictsWith?: string[];
  /**
   * The feature never survives a page load: `applyAll()` skips it and does not persist its level,
   * so a reload always finds it off. Starting it again on its own, without the visitor asking,
   * would be wrong — e.g. it captures the microphone or camera, or otherwise needs a fresh gesture
   * each time. `setProfile()` skips it too, for the same reason: a click on a profile tile is not a
   * gesture on this feature's own tile, so a profile that names an ephemeral feature must neither
   * apply nor persist it.
   */
  ephemeral?: boolean;
  /** Return false when the environment cannot support the feature; it is then hidden and never applied. */
  isSupported?(doc: Document): boolean;
  apply(ctx: FeatureContext, level: number): void;
  teardown(ctx: FeatureContext): void;
}

export interface ProfileDefinition {
  id: string;
  labelKey: MessageKey;
  features: Record<string, number>;
}

export interface Registry {
  get(id: string): FeatureDefinition | undefined;
  list(): FeatureDefinition[];
  conflicts(id: string): string[];
}

export function createRegistry(definitions: FeatureDefinition[]): Registry {
  const map = new Map<string, FeatureDefinition>();
  for (const def of definitions) {
    if (map.has(def.id)) throw new Error(`[pulxon] duplicate feature id: ${def.id}`);
    if (!Number.isInteger(def.levels) || def.levels < 1) {
      throw new Error(`[pulxon] invalid levels for feature: ${def.id}`);
    }
    if (def.levelLabelKeys && def.levelLabelKeys.length !== def.levels) {
      throw new Error(`[pulxon] levelLabelKeys must match levels for feature: ${def.id}`);
    }
    map.set(def.id, def);
  }

  const conflictMap = new Map<string, Set<string>>();
  const link = (from: string, to: string): void => {
    const set = conflictMap.get(from) ?? new Set<string>();
    set.add(to);
    conflictMap.set(from, set);
  };
  for (const def of definitions) {
    for (const other of def.conflictsWith ?? []) {
      if (other === def.id) throw new Error(`[pulxon] feature cannot conflict with itself: ${def.id}`);
      // A conflict with a feature that is not registered cannot happen, so it is ignored.
      if (!map.has(other)) continue;
      link(def.id, other);
      link(other, def.id);
    }
  }

  return {
    get: (id) => map.get(id),
    list: () => [...map.values()],
    conflicts: (id) => [...(conflictMap.get(id) ?? [])],
  };
}
